import {
  IonCheckbox,
  IonContent,
  IonHeader,
  IonModal,
  IonRouterLink,
  IonSearchbar
} from '@ionic/react';
import React, { useEffect, useState } from 'react';

import { Empty } from '@/components/common';
import { SELECT_MODAL_OTHERS } from '@/utils/constants';

import './SelectModal.css';

interface SelectModalProps {
  isOpen: boolean;
  presentingElement?: HTMLElement | undefined;
  title?: string | React.ReactNode;
  options: string[];
  getLabel?: (option: string) => string;
  isSelected: (option: string) => boolean;
  isPreSelected?: (option: string) => boolean;
  onSelect: (option: string, isSelected: boolean) => void;
  onDismiss: () => void;
  showOthers?: boolean;
  multiple?: boolean;
}

const SelectModal: React.FC<SelectModalProps> = ({
  isOpen,
  presentingElement,
  title = 'Select an option',
  options,
  getLabel = (option: string) => option,
  isSelected,
  isPreSelected = () => false,
  onSelect,
  onDismiss,
  showOthers = true,
  multiple = false
}) => {
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const [filteredOptions, setFilteredOptions] = useState<string[]>(options);

  useEffect(() => {
    filterOptions(searchQuery);
  }, [searchQuery, options]);

  const emptyMessage = (
    <>
      No results found for "{searchQuery}",
      <br />
      you can select Others.
    </>
  );

  const handleSelect = (option: string) => {
    onSelect(option, isSelected(option));
  };

  const onSearch = (event: CustomEvent) => {
    setSearchQuery(event.detail.value);
  };

  const filterOptions = (searchQuery: string | null | undefined) => {
    if (!searchQuery) {
      setFilteredOptions(options);
    } else {
      const normalizedQuery = searchQuery.toLowerCase();
      setFilteredOptions(
        options.filter((option) =>
          getLabel(option).toLowerCase().includes(normalizedQuery)
        )
      );
    }
  };

  const isOthersSelected = isSelected(SELECT_MODAL_OTHERS);

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onDismiss}
      presentingElement={presentingElement}
    >
      <IonHeader className="select-modal__header">
        <h1 className="select-modal__title">{title}</h1>
        {multiple && (
          <p className="select-modal__subtitle">
            You can select multiple options
          </p>
        )}
        <div className="select-modal__search-bar">
          <IonSearchbar onIonInput={onSearch}></IonSearchbar>
          <IonRouterLink onClick={onDismiss}>Done</IonRouterLink>
        </div>
      </IonHeader>
      <IonContent className="select-modal__content">
        <div className="select-modal__options">
          {filteredOptions.sort().map((option, index) => {
            if (option === SELECT_MODAL_OTHERS && showOthers) return null;

            const selected = isSelected(option);
            const preSelected = isPreSelected(option);

            return (
              <IonCheckbox
                key={`selectModalOption-${index}`}
                className={`select-modal__option ${selected ? 'select-modal__option--selected' : ''} ${preSelected ? 'select-modal__option--pre-selected' : ''}`}
                checked={selected}
                onClick={() => handleSelect(option)}
              >
                <span className="option-label ion-text-wrap">
                  {getLabel(option)}
                </span>
              </IonCheckbox>
            );
          })}
          {showOthers && (
            <IonCheckbox
              key="selectModalOption-others"
              className={`select-modal__option ${isOthersSelected ? 'select-modal__option--selected' : ''}`}
              checked={isOthersSelected}
              onClick={() => handleSelect(SELECT_MODAL_OTHERS)}
            >
              <span className="option-label ion-text-wrap">
                {getLabel(SELECT_MODAL_OTHERS)}
              </span>
            </IonCheckbox>
          )}
        </div>
        {filteredOptions.length === 0 && <Empty message={emptyMessage} />}
      </IonContent>
    </IonModal>
  );
};

export default SelectModal;
